import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { toggleBlackBoxFileUploader } from 'store/entities/blackbox';
import { blackboxUpload } from 'store/entities/blackbox/thunks';

import Button from 'components/UIComponents/Button';
import { Column, Row, Section } from 'components/UIComponents/Layout';
import Modal, { ModalCard } from 'components/UIComponents/Modal';
import LoadingSpinner from 'components/UIComponents/LoadingSpinner';
import OrganizationField from './FormFields/OrganizationField';
import RegistryCodeField from './FormFields/RegistryCodeField';
import ContactField from './FormFields/ContactField';
import ExplanationField from './FormFields/ExplanationField';
import FileUploadField from './FormFields/FileUploadField';
import UploadedFileList from './FormFields/UploadedFileList';
import './style.scss';
import { notifyError } from 'utils/notifications';

export default () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { fileUploaderOpen, fileUploading } = useSelector(
    state => state.blackbox
  );

  const { acceptedFiles, fileRejections, ...dropzoneProps } = useDropzone({
    maxFiles: 1,
    disabled: fileUploading,
    accept: ['.txt', '.csv', '.xls', '.xlsx', '.json', '.xml'],
  });

  const { handleSubmit, control, errors, setValue } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      file: null,
      organizationName: '',
      registryCode: '',
      contactName: '',
      explanation: '',
    },
  });

  const closeUploader = () => {
    dispatch(toggleBlackBoxFileUploader(false));
    acceptedFiles.splice(0, 1);
  };

  const prepareFormData = values => {
    const formData = new FormData();
    const file = acceptedFiles[0];
    formData.append('file', file);
    formData.append('organizationName', values.organizationName);
    formData.append('registryCode', values.registryCode);
    formData.append('contactName', values.contactName);
    formData.append('explanation', values.explanation);
    return formData;
  };

  const upload = async payload => {
    const uploadResult = await dispatch(blackboxUpload(payload));
    if (blackboxUpload.fulfilled.match(uploadResult)) {
      closeUploader();
    }
  };

  const submitBlackBoxUpload = async values => {
    const file = acceptedFiles[0];
    if (file == null) {
      notifyError(t('datagate.external.fileUpload.form.file.required'));
    } else {
      upload(prepareFormData(values));
    }
  };

  const handleFileError = useCallback(
    (errorCode, translation) => {
      if (
        fileRejections.find(rejection =>
          rejection.errors.find(error => error.code === errorCode)
        )
      ) {
        notifyError(t(translation));
        acceptedFiles.splice(0, 1);
        fileRejections.splice(0, 1);
        setValue('file', null);
      }
    },
    [fileRejections, acceptedFiles, setValue, t]
  );

  useEffect(() => {
    handleFileError(
      'too-many-files',
      'datagate.external.fileUpload.multiple.files.not.allowed'
    );
    handleFileError(
      'file-invalid-type',
      'datagate.external.fileUpload.fileType.not.allowed'
    );
  }, [fileRejections, acceptedFiles, t, handleFileError]);

  const hasFile = acceptedFiles.length > 0;

  return (
    <Modal
      show={fileUploaderOpen}
      onClose={closeUploader}
      closeOnBlur={!fileUploading}
    >
      <form
        className="blackBoxUploadForm"
        onSubmit={handleSubmit(submitBlackBoxUpload)}
      >
        <ModalCard>
          <LoadingSpinner
            loading={fileUploading}
            message={t(
              'datagate.external.submission.fileUpload.message.fileUploading'
            )}
          />
          <ModalCard.Head>
            <ModalCard.Title>
              {t('datagate.external.submission.fileUpload.title')}
            </ModalCard.Title>
          </ModalCard.Head>
          <ModalCard.Body>
            <Section className="vertical-padding-md">
              <Row>
                <Column size={6}>
                  <OrganizationField
                    name="organizationName"
                    control={control}
                    errors={errors}
                  />
                </Column>
                <Column size={6}>
                  <RegistryCodeField
                    name="registryCode"
                    control={control}
                    errors={errors}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <ContactField
                    name="contactName"
                    control={control}
                    errors={errors}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <ExplanationField
                    name="explanation"
                    control={control}
                    errors={errors}
                  />
                </Column>
              </Row>
              <Row>
                <Column>
                  <FileUploadField
                    name="file"
                    control={control}
                    errors={errors}
                    dropzoneProps={dropzoneProps}
                  />
                </Column>
              </Row>
              {hasFile && (
                <Row gapless>
                  <Column>
                    <UploadedFileList acceptedFiles={acceptedFiles} />
                  </Column>
                </Row>
              )}
            </Section>
          </ModalCard.Body>
          <ModalCard.Foot>
            <Button
              color="primary"
              type="button"
              disabled={fileUploading}
              onClick={closeUploader}
              outlined
              id="closeUploaderButton"
            >
              &#215;{' '}
              {t('datagate.external.submission.fileUpload.button.cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={fileUploading}
              id="confirmUploaderButton"
            >
              {t('datagate.external.submission.fileUpload.button.upload')}
            </Button>
          </ModalCard.Foot>
        </ModalCard>
      </form>
    </Modal>
  );
};
